import {
  Alert,
  Avatar,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { sameOriginImageURL } from "@shared/helpers/constants";
import "dayjs/locale/he";
import { useQuery } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { type FC, useEffect, useMemo, useState } from "react";
import { getErrorMessage } from "../../auth/Auth";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import {
  clampDateToOnFromChange,
  registrationTrackingDefaultDay,
  validateRegistrationDateRange,
} from "../opportunities-table/registrationTrackingDateUtils";
import { cooperationLevelShortLabel } from "./cooperation-level";
import { partnerOrgHandledByLabel } from "@shared/helpers/partner-organization-handled-by";
import { partnerOrgNeedsContact } from "@shared/helpers/partner-organization-contact";
import { comparePartnerOrgCoverage } from "@shared/helpers/partner-organization-coverage-sort";
import {
  formatPartnerOrgActivityDate,
  formatPartnerOrgFollowUpDate,
} from "./partner-organization-activity";
import {
  fetchPartnerOrganizationCoverage,
  type PartnerOrganizationCoverageRow,
} from "./partnerOrganizationCoverageApi";

export type CoverageSortMode =
  | "priority"
  | "fullTasks"
  | "missing"
  | "registrations";

const COVERAGE_PAGE_SIZE = 15;

interface PartnerOrganizationsCoverageProps {
  partnerOrganizationIds?: string[];
  onOpenOrganization: (
    partnerOrganizationId: string,
    coverageRow?: PartnerOrganizationCoverageRow,
    registrationDeadlineFrom?: string,
  ) => void;
  onGoToCatalog?: () => void;
}

function coverageDatesStorageKey(organizationId: string) {
  return `partner-org-coverage-dates-${organizationId}`;
}

function defaultCoverageDates(): { dateFrom: Dayjs; dateTo: Dayjs } {
  const dateTo = dayjs().startOf("day");
  return {
    dateFrom: dateTo.subtract(29, "day"),
    dateTo,
  };
}

function readStoredCoverageDates(
  organizationId: string,
): { dateFrom: Dayjs; dateTo: Dayjs } {
  const fallback = defaultCoverageDates();
  try {
    const raw = sessionStorage.getItem(coverageDatesStorageKey(organizationId));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as { dateFrom?: string; dateTo?: string };
    const dateFrom = dayjs(parsed.dateFrom, "YYYY-MM-DD", true);
    const dateTo = dayjs(parsed.dateTo, "YYYY-MM-DD", true);
    if (
      !dateFrom.isValid() ||
      !dateTo.isValid() ||
      validateRegistrationDateRange(dateFrom, dateTo)
    ) {
      return fallback;
    }
    return { dateFrom, dateTo };
  } catch {
    return fallback;
  }
}

function slotFillRate(row: {
  assignedSeats: number;
  slotCount: number;
}): number {
  return row.slotCount > 0 ? row.assignedSeats / row.slotCount : 0;
}

function coverageFillPercent(row: PartnerOrganizationCoverageRow): number {
  return row.slotCount === 0 ? 0 : (row.fillRatePercent ?? 0);
}

function sortCoverageRows(
  rows: PartnerOrganizationCoverageRow[],
  mode: CoverageSortMode,
): PartnerOrganizationCoverageRow[] {
  return [...rows].sort((a, b) => {
    if (mode === "priority") {
      const order = comparePartnerOrgCoverage(
        {
          cooperationLevel: a.cooperationLevel,
          fillRatePercent: coverageFillPercent(a),
        },
        {
          cooperationLevel: b.cooperationLevel,
          fillRatePercent: coverageFillPercent(b),
        },
      );
      if (order !== 0) return order;
    } else if (mode === "missing") {
      if (a.missingSeats !== b.missingSeats) {
        return b.missingSeats - a.missingSeats;
      }
    } else if (mode === "fullTasks") {
      const fullA = a.fullMissionPercent ?? 0;
      const fullB = b.fullMissionPercent ?? 0;
      if (fullA !== fullB) return fullA - fullB;
    } else if (mode === "registrations") {
      const fillA = slotFillRate(a);
      const fillB = slotFillRate(b);
      if (fillA !== fillB) return fillA - fillB;
    }
    if (a.missingSeats !== b.missingSeats) {
      return b.missingSeats - a.missingSeats;
    }
    return a.name.localeCompare(b.name, "he");
  });
}

export const PartnerOrganizationsCoverage: FC<
  PartnerOrganizationsCoverageProps
> = ({ partnerOrganizationIds, onOpenOrganization, onGoToCatalog }) => {
  const { currOrganization } = useOrganization();
  const [sortMode, setSortMode] = useState<CoverageSortMode>("priority");
  const [onlyWithoutVolunteers, setOnlyWithoutVolunteers] = useState(false);
  const [nameQuery, setNameQuery] = useState("");
  const [page, setPage] = useState(0);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(
    () => defaultCoverageDates().dateFrom,
  );
  const [dateTo, setDateTo] = useState<Dayjs | null>(
    () => defaultCoverageDates().dateTo,
  );

  useEffect(() => {
    if (!currOrganization?.id) {
      setDateFrom(null);
      setDateTo(null);
      return;
    }
    const stored = readStoredCoverageDates(currOrganization.id);
    setDateFrom(stored.dateFrom);
    setDateTo(stored.dateTo);
  }, [currOrganization?.id]);

  useEffect(() => {
    if (!currOrganization?.id || !dateFrom || !dateTo) return;
    if (validateRegistrationDateRange(dateFrom, dateTo)) return;
    sessionStorage.setItem(
      coverageDatesStorageKey(currOrganization.id),
      JSON.stringify({
        dateFrom: dateFrom.format("YYYY-MM-DD"),
        dateTo: dateTo.format("YYYY-MM-DD"),
      }),
    );
  }, [currOrganization?.id, dateFrom, dateTo]);

  const dateRangeError = useMemo(
    () => validateRegistrationDateRange(dateFrom, dateTo),
    [dateFrom, dateTo],
  );

  const rangeReady = !dateRangeError && !!dateFrom && !!dateTo;

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: [
      "partner-organization-coverage",
      currOrganization?.id ?? "",
      dateFrom?.format("YYYY-MM-DD") ?? "",
      dateTo?.format("YYYY-MM-DD") ?? "",
      partnerOrganizationIds,
    ],
    queryFn: () =>
      fetchPartnerOrganizationCoverage({
        organizationId: currOrganization!.id,
        dateFrom: dateFrom!.format("YYYY-MM-DD"),
        dateTo: dateTo!.format("YYYY-MM-DD"),
        partnerOrganizationIds,
      }),
    enabled:
      !!currOrganization?.id &&
      (partnerOrganizationIds == null || partnerOrganizationIds.length > 0) &&
      rangeReady,
    staleTime: 1000 * 30,
  });

  const namedRows = useMemo(() => {
    const q = nameQuery.trim().toLocaleLowerCase("he");
    return (data?.organizations ?? []).filter((row) => {
      if (onlyWithoutVolunteers && row.registrationsInRange > 0) return false;
      return q ? row.name.toLocaleLowerCase("he").includes(q) : true;
    });
  }, [data?.organizations, nameQuery, onlyWithoutVolunteers]);

  const visibleRows = useMemo(
    () => sortCoverageRows(namedRows, sortMode),
    [namedRows, sortMode],
  );

  useEffect(() => {
    setPage(0);
  }, [
    nameQuery,
    sortMode,
    partnerOrganizationIds,
    onlyWithoutVolunteers,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(visibleRows.length / COVERAGE_PAGE_SIZE) - 1,
    );
    if (page > maxPage) setPage(maxPage);
  }, [visibleRows.length, page]);

  const pagedRows = useMemo(() => {
    const start = page * COVERAGE_PAGE_SIZE;
    return visibleRows.slice(start, start + COVERAGE_PAGE_SIZE);
  }, [visibleRows, page]);

  if (!currOrganization?.id) {
    return (
      <Stack alignItems="center" py={6}>
        <Skeleton variant="rounded" width="80%" height={48} />
      </Stack>
    );
  }

  return (
    <Stack gap={1.25} flex={1} minHeight={0} sx={{ px: 2, pb: 2 }}>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
          <Chip
            label="בלי מתנדבים בטווח"
            title="ארגונים עם משימות בטווח שלא קיבלו אף מתנדב"
            clickable
            color={onlyWithoutVolunteers ? "secondary" : "default"}
            variant={onlyWithoutVolunteers ? "filled" : "outlined"}
            onClick={() => setOnlyWithoutVolunteers((on) => !on)}
            sx={{ borderRadius: "1rem", fontWeight: 600 }}
          />
          <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="he"
              localeText={
                heIL.components.MuiLocalizationProvider.defaultProps.localeText
              }
            >
              <DatePicker
                label="מתאריך"
                value={dateFrom}
                maxDate={dateTo ?? undefined}
                onChange={(value) => {
                  setDateFrom(value);
                  setDateTo((prev) => clampDateToOnFromChange(value, prev));
                }}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: "small" },
                  layout: {
                    sx: {
                      "& .MuiMonthCalendar-root": { direction: "ltr" },
                      "& .MuiYearCalendar-root": { direction: "ltr" },
                    },
                  },
                }}
              />
              <DatePicker
                label="עד תאריך"
                value={dateTo}
                minDate={dateFrom ?? undefined}
                maxDate={registrationTrackingDefaultDay()}
                onChange={setDateTo}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { size: "small" },
                  layout: {
                    sx: {
                      "& .MuiMonthCalendar-root": { direction: "ltr" },
                      "& .MuiYearCalendar-root": { direction: "ltr" },
                    },
                  },
                }}
              />
          </LocalizationProvider>
          <TextField
            size="small"
            label="חיפוש ארגון"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
          />
        </Stack>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={sortMode}
          onChange={(_, value: CoverageSortMode | null) => {
            if (value) setSortMode(value);
          }}
          sx={{
            "& .MuiToggleButton-root": {
              borderRadius: "1rem",
              textTransform: "none",
              color: "secondary.main",
              px: 1.5,
            },
          }}
        >
          <ToggleButton value="priority" title="רצינות 40%, מילוי 60%">
            עדיפות
          </ToggleButton>
          <ToggleButton value="missing">לפי חוסר</ToggleButton>
          <ToggleButton value="registrations">לפי מילוי</ToggleButton>
          <ToggleButton value="fullTasks">לפי משימות מלאות</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {dateRangeError && (
        <Alert severity="warning">{dateRangeError}</Alert>
      )}

      {isError && !dateRangeError && (
        <Alert severity="error">{getErrorMessage(error)}</Alert>
      )}

      {isFetching && !isLoading ? (
        <Typography variant="caption" color="text.secondary">
          מעדכן...
        </Typography>
      ) : null}

      {!dateRangeError && isLoading && !data && (
        <Stack gap={1}>
          {[0, 1, 2, 3].map((key) => (
            <Skeleton key={key} variant="rounded" height={72} />
          ))}
        </Stack>
      )}

      {!dateRangeError && !isError && data && (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            flex: "1 1 auto",
            minHeight: "55vh",
            overflow: "auto",
            borderRadius: "12px",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ארגון</TableCell>
                <TableCell>מטפל</TableCell>
                <TableCell align="center">מתנדבים בטווח</TableCell>
                <TableCell align="center">חוסר עכשיו</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.length === 0 ? (
                <TableRow>
                    <TableCell colSpan={4} align="center">
                    <Stack gap={1} alignItems="center" sx={{ py: 2 }}>
                      <Typography>
                        {data.openMissionsInRange === 0
                          ? "אין משימות פתוחות בטווח הזה"
                          : onlyWithoutVolunteers
                            ? "אין ארגונים בלי מתנדבים בטווח"
                            : "אין ארגונים שותפים עם משימות לפי הסינון הנוכחי"}
                      </Typography>
                      {onGoToCatalog ? (
                        <Typography
                          variant="body2"
                          color="secondary.main"
                          sx={{ cursor: "pointer" }}
                          onClick={onGoToCatalog}
                        >
                          מעבר לכל הארגונים
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => {
                  const logoId = (row.logoImageId || "").trim();
                  const coopLabel = cooperationLevelShortLabel(
                    row.cooperationLevel,
                  );
                  const handledByLabel = partnerOrgHandledByLabel(row.handledBy);
                  const needsContact = partnerOrgNeedsContact({
                    nextFollowUpAt: row.nextFollowUpAt,
                    lastCommunicationAt: row.lastCommunicationAt,
                    cooperationLevel: row.cooperationLevel,
                  });
                  return (
                    <TableRow
                      key={row.partnerOrganizationId}
                      hover
                      sx={{
                        cursor: "pointer",
                        bgcolor:
                          row.missingSeats > 0
                            ? "rgba(211, 47, 47, 0.04)"
                            : undefined,
                      }}
                      onClick={() =>
                        onOpenOrganization(
                          row.partnerOrganizationId,
                          row,
                          dateFrom?.format("YYYY-MM-DD"),
                        )
                      }
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1.5}>
                          <Avatar
                            src={logoId ? sameOriginImageURL(logoId) : undefined}
                            alt={row.name}
                            imgProps={{ referrerPolicy: "no-referrer" }}
                            sx={{ width: 40, height: 40 }}
                          />
                          <Stack sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} noWrap>
                              {row.name}
                            </Typography>
                            <Stack
                              direction="row"
                              gap={0.75}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {row.missionCount} מופעים
                                {coopLabel ? ` · ${coopLabel}` : ""}
                              </Typography>
                              {needsContact ? (
                                <Typography variant="caption" color="error.main">
                                  דורש מגע
                                </Typography>
                              ) : null}
                            </Stack>
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack gap={0.5}>
                          <Typography variant="body2">
                            {handledByLabel ?? "אין צוות"}
                            {row.handlingManagerName
                              ? ` · ${row.handlingManagerName}`
                              : ""}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            כניסה{" "}
                            {formatPartnerOrgActivityDate(row.lastLoginAt)}
                            {" · "}
                            צורך{" "}
                            {formatPartnerOrgActivityDate(row.lastNeedAt)}
                            {" · "}
                            תקשורת{" "}
                            {formatPartnerOrgActivityDate(
                              row.lastCommunicationAt,
                            )}
                            {" · "}
                            חזרה{" "}
                            {formatPartnerOrgFollowUpDate(row.nextFollowUpAt)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          color={
                            row.registrationsInRange === 0
                              ? "error.main"
                              : "text.primary"
                          }
                        >
                          {row.registrationsInRange}
                        </Typography>
                        {row.emptyMissionCount > 0 ? (
                          <Typography variant="caption" color="error.main">
                            מופעים בלי מתנדבים: {row.emptyMissionCount}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          color={
                            row.missingSeats > 0 ? "error.main" : "text.secondary"
                          }
                        >
                          {row.missingSeats}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={visibleRows.length}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={COVERAGE_PAGE_SIZE}
            rowsPerPageOptions={[COVERAGE_PAGE_SIZE]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from} עד ${to} מתוך ${count}`
            }
            sx={{
              "& .MuiTablePagination-actions .MuiSvgIcon-root": {
                transform: "rotate(180deg)",
              },
            }}
          />
        </TableContainer>
      )}
    </Stack>
  );
};
